import { useCallback, useEffect, useMemo, useState } from "react";
import { usePDF } from "@react-pdf/renderer";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BadgeOutlined,
  ContactEmergency,
  CreditCard,
  Description,
  Fingerprint,
  ImageOutlined,
  LocalHospital,
  PermIdentity,
  Portrait,
  Print,
  School,
  Search,
  Shield,
  VerifiedUser,
  VideoLibrary,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import CustomDialog from "../../components/modals/CustomDialog";
import Toast from "../../components/Layout/Toast";
import { countries } from "../../components/dashboard/CountrySelect";
import { selectAuth } from "../auth/authSlice";
import { AuthUser, ClientTypes, Role } from "../auth/types/authType";
import { SaudiApplication } from "../../components/reports/embassy/SaudiApplication";
import { CompanyFilter } from "../../components/filter/CompanyFilter";
import {
  applyOfficeSelectionToEmployee,
  officePartnerSourceFromContext,
  resolveDefaultSelectOffice,
} from "./utils/officePartners";
import { useOfficePartnerContext } from "../calling/hooks/useOfficePartnerContext";
import {
  changeEmployeeStatus,
  createEmployee,
  fetchEmployees,
  getEmployee,
  removeEmployee,
  resetSelectedEmployee,
  resetSuccess,
  selectProcesses,
  setSelectedEmployee as setReduxSelectedEmployee,
} from "./processSlices";
import { ProcessStages } from "./components/ProcessStages";
import { MedicalDialog } from "./components/MedicalDialog";
import { EmployeeLimitedEditDialog } from "./components/EmployeeLimitedEditDialog";
import { EmployeeStatusHistoryDialog } from "./components/EmployeeStatusHistoryDialog";
import { EmployeeRemarksDialog } from "./components/EmployeeRemarksDialog";
import { EmployeeListRowActions } from "./components/EmployeeListRowActions";
import { EmployeeFollowUpSummary } from "../follow-ups/components/EmployeeFollowUpSummary";
import { EmployeeMediaCard } from "./components/EmployeeMediaCard";
import { VisaEntry } from "./VisaEntry";
import { Company } from "../setups/types/setupTypes";
import {
  Employee,
  EmployeeStatus,
  NotificationMessageTypes,
  PhotoTypes,
} from "./types/processTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import {
  MEDICAL_ACTION_TABS,
  resolveEmployeeStatusTab,
} from "./utils/employeeStatusTab";

type EmployeeDetailViewProps = {
  employeeId?: string;
};

const formatValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const formatDateValue = (value?: Date | string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const EmployeeInfoField = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    label={label}
    value={formatValue(value)}
    InputProps={{
      readOnly: true,
    }}
    InputLabelProps={{ shrink: true }}
    sx={{
      "& .MuiInputBase-root": {
        borderRadius: 1.5,
        bgcolor: "background.default",
      },
      "& .MuiInputBase-input": {
        fontWeight: 500,
        color: "primary.main",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "divider",
      },
      "& .MuiFormLabel-root": {
        fontWeight: 600,
      },
    }}
  />
);

export const EmployeeDetailView = ({ employeeId }: EmployeeDetailViewProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const { context: officePartnerContext } = useOfficePartnerContext();
  const {
    selectedEmployee,
    error: processError,
    success: processSuccess,
  } = useAppSelector(selectProcesses);
  const [searchText, setSearchText] = useState("");
  const [searchOptions, setSearchOptions] = useState<Employee[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);
  const [openChangeStatus, setOpenChangeStatus] = useState(false);
  const [openMedical, setOpenMedical] = useState(false);
  const [openLimitedEdit, setOpenLimitedEdit] = useState(false);
  const [openStatusHistory, setOpenStatusHistory] = useState(false);
  const [statusHistoryEmployee, setStatusHistoryEmployee] =
    useState<Employee | null>(null);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [openRemarks, setOpenRemarks] = useState(false);
  const [remarksEmployee, setRemarksEmployee] = useState<Employee | null>(null);
  const [remarksLoading, setRemarksLoading] = useState(false);
  const [openVisa, setOpenVisa] = useState(false);
  const [openAgentSelect, setOpenAgentSelect] = useState(false);
  const [agentSelectEmployee, setAgentSelectEmployee] =
    useState<Employee | null>(null);
  const [agentForSelect, setAgentForSelect] = useState<Company | undefined>();
  const [openStop, setOpenStop] = useState(false);
  const [stopEmployee, setStopEmployee] = useState<Employee | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState("");
  const [actionEmployee, setActionEmployee] = useState<Employee>();
  const [videoPreview, setVideoPreview] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const application = SaudiApplication({
    employee: selectedEmployee,
    user: user as AuthUser,
  });
  const [instance, updateInstance] = usePDF({
    document: application,
  });

  useEffect(() => {
    if (selectedEmployee) {
      updateInstance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee]);

  const canPrintFirstPage = useMemo(() => {
    if (!selectedEmployee) return false;
    return Boolean(
      selectedEmployee.visa &&
      selectedEmployee.passportNumber &&
      selectedEmployee.passportNumber.length > 0 &&
      selectedEmployee.embassyProcess?.enjazNumber,
    );
  }, [selectedEmployee]);

  function printFirstPage() {
    if (!canPrintFirstPage || !instance.url) return;

    const iframe = document.createElement("iframe");
    document.body.appendChild(iframe);
    iframe.style.display = "none";
    iframe.src = instance.url;
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow?.print();
      }, 1);
    };
  }

  const effectiveEmployeeId = employeeId || id;
  const isSearchOnlyMode = effectiveEmployeeId === "00000000";
  const isValidId = Boolean(
    effectiveEmployeeId && effectiveEmployeeId.length > 10,
  );

  useEffect(() => {
    let active = true;
    const loadDetail = async () => {
      if (isValidId && effectiveEmployeeId) {
        setIsFetchingDetail(true);
        await dispatch(getEmployee(effectiveEmployeeId));
        if (active) setIsFetchingDetail(false);
        return;
      }

      dispatch(resetSelectedEmployee());
      setIsFetchingDetail(false);
    };
    loadDetail();
    return () => {
      active = false;
    };
  }, [dispatch, effectiveEmployeeId, isValidId]);

  useEffect(() => {
    const term = searchText.trim();
    if (term.length <= 1) {
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }

    let active = true;
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const result = await dispatch(
        fetchEmployees({
          skip: 0,
          take: 8,
          searchText: term,
        }),
      );
      if (!active) return;
      if (fetchEmployees.fulfilled.match(result)) {
        setSearchOptions(result.payload?.employees || []);
      } else {
        setSearchOptions([]);
      }
      setSearchLoading(false);
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [dispatch, searchText]);

  const handleSelectEmployee = async (employee: Employee | null) => {
    if (!employee?._id) return;

    setSearchText(
      `${employee.givenName || ""} ${employee.surName || ""}`.trim(),
    );
    setSearchOptions([]);

    if (employeeId) {
      setIsFetchingDetail(true);
      await dispatch(getEmployee(employee._id));
      setIsFetchingDetail(false);
      return;
    }

    navigate(`/app/employee-detail/${employee._id}`);
  };

  const fullName = useMemo(() => {
    const name =
      `${selectedEmployee?.givenName || ""} ${selectedEmployee?.surName || ""}`.trim();
    return (
      name ||
      selectedEmployee?.additionalData?.fullNameAmharic ||
      "Unnamed employee"
    );
  }, [
    selectedEmployee?.additionalData?.fullNameAmharic,
    selectedEmployee?.givenName,
    selectedEmployee?.surName,
  ]);

  const appliedCountryMeta = useMemo(() => {
    const appliedCountry = selectedEmployee?.additionalData?.appliedCountry;
    if (!appliedCountry) return null;

    const matchedCountry = countries.find(
      (country) => country.label === appliedCountry,
    );

    return {
      label: te(appliedCountry),
      flagCode: matchedCountry?.code?.toLowerCase(),
    };
  }, [selectedEmployee?.additionalData?.appliedCountry, te]);

  const showLoading = isFetchingDetail;
  const hasEmployee = Boolean(selectedEmployee?._id);

  const activeStatusTab = useMemo(
    () =>
      selectedEmployee
        ? resolveEmployeeStatusTab(selectedEmployee)
        : "available",
    [selectedEmployee],
  );

  const showMedicalAction =
    MEDICAL_ACTION_TABS.includes(activeStatusTab) &&
    isPrivilegedTransaction(user?.roles as Role[], "Records.AddMedical");

  const refreshDetail = useCallback(async () => {
    if (!effectiveEmployeeId || !isValidId) return;
    await dispatch(getEmployee(effectiveEmployeeId));
  }, [dispatch, effectiveEmployeeId, isValidId]);

  const handleClickOpenDelete = (id: string) => {
    setDeleteEmployeeId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const deleteEmployee = async () => {
    setOpenDelete(false);
    await dispatch(removeEmployee(deleteEmployeeId));
    navigate("/app/employees");
  };

  const playEmployeeVideo = (row: Employee) => {
    if (!row.employeeVideo) return;
    const name = `${row.givenName ?? ""} ${row.surName ?? ""}`.trim();
    setVideoPreview({ url: row.employeeVideo, name: name || "Employee video" });
  };

  const changeCandidateStatus = (employee: Employee) => {
    dispatch(resetSuccess());
    dispatch(setReduxSelectedEmployee(employee));
    setActionEmployee(employee);
    setOpenChangeStatus(true);
  };

  const openMedicalDialog = (employee: Employee) => {
    dispatch(resetSuccess());
    dispatch(setReduxSelectedEmployee(employee));
    setActionEmployee(employee);
    setOpenMedical(true);
  };

  const openLimitedEditDialog = async (employee: Employee) => {
    dispatch(resetSuccess());
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      dispatch(setReduxSelectedEmployee(result.payload));
      setActionEmployee(result.payload);
    } else {
      dispatch(setReduxSelectedEmployee(employee));
      setActionEmployee(employee);
    }
    setOpenLimitedEdit(true);
  };

  const openStatusHistoryDialog = async (employee: Employee) => {
    setStatusHistoryEmployee(employee);
    setOpenStatusHistory(true);
    setStatusHistoryLoading(true);
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      setStatusHistoryEmployee(result.payload);
    }
    setStatusHistoryLoading(false);
  };

  const dialogCloseStatusHistory = () => {
    setOpenStatusHistory(false);
    setStatusHistoryEmployee(null);
    setStatusHistoryLoading(false);
  };

  const openRemarksDialog = async (employee: Employee) => {
    setRemarksEmployee(employee);
    setOpenRemarks(true);
    setRemarksLoading(true);
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      setRemarksEmployee(result.payload);
    }
    setRemarksLoading(false);
  };

  const dialogCloseRemarks = () => {
    setOpenRemarks(false);
    setRemarksEmployee(null);
    setRemarksLoading(false);
  };

  const selectCandidateForVisa = async (employee: Employee) => {
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      setActionEmployee(result.payload);
    } else {
      setActionEmployee(employee);
    }
    setOpenVisa(true);
  };

  const openAgentSelectDialog = (employee: Employee) => {
    const partnerSource = officePartnerSourceFromContext(
      officePartnerContext,
      user ?? undefined,
    );
    setAgentForSelect(
      resolveDefaultSelectOffice({
        employee,
        user: user ?? undefined,
        source: partnerSource,
      }),
    );
    setAgentSelectEmployee(employee);
    setOpenAgentSelect(true);
  };

  const dialogCloseChangeStatus = useCallback(
    async (saved = false) => {
      setOpenChangeStatus(false);
      dispatch(resetSuccess());
      if (saved) await refreshDetail();
    },
    [dispatch, refreshDetail],
  );

  const dialogCloseMedical = useCallback(
    async (saved = false) => {
      setOpenMedical(false);
      dispatch(resetSuccess());
      if (saved) await refreshDetail();
    },
    [dispatch, refreshDetail],
  );

  const dialogCloseLimitedEdit = useCallback(
    async (saved = false) => {
      setOpenLimitedEdit(false);
      dispatch(resetSuccess());
      if (saved) await refreshDetail();
    },
    [dispatch, refreshDetail],
  );

  const dialogCloseVisa = useCallback(async () => {
    setOpenVisa(false);
    await refreshDetail();
  }, [refreshDetail]);

  const handleStopEmployee = async () => {
    if (!stopEmployee?._id) return;
    const result = await dispatch(
      changeEmployeeStatus({
        _id: String(stopEmployee._id),
        status: EmployeeStatus.stopped,
      }),
    );
    setOpenStop(false);
    setStopEmployee(null);
    if (changeEmployeeStatus.fulfilled.match(result)) {
      await refreshDetail();
    }
  };

  const restoreToAvailable = async (employee: Employee) => {
    const result = await dispatch(
      changeEmployeeStatus({
        _id: String(employee._id),
        status: EmployeeStatus.new,
      }),
    );
    if (changeEmployeeStatus.fulfilled.match(result)) {
      await refreshDetail();
    }
  };

  const justSelect = async () => {
    if (!agentSelectEmployee) return;
    const result = await dispatch(getEmployee(String(agentSelectEmployee._id)));
    if (!getEmployee.fulfilled.match(result)) return;
    const { currentStatusDate: _omitStatusDate, ...employeeBase } =
      result.payload as Employee;
    const partnerSource = officePartnerSourceFromContext(
      officePartnerContext,
      user ?? undefined,
    );
    const office = applyOfficeSelectionToEmployee({
      employee: employeeBase,
      selectedOffice: agentForSelect,
      source: partnerSource,
    });
    const values: Employee = {
      ...employeeBase,
      status: EmployeeStatus.selected,
      notificationMessage: NotificationMessageTypes.selected as string,
      partner: office.partner,
      owner: office.owner,
      parentCompany: office.parentCompany,
      additionalData: {
        ...employeeBase.additionalData,
        appliedCountry: office.appliedCountry,
      },
    };
    const createResult = await dispatch(createEmployee(values));
    setOpenAgentSelect(false);
    setAgentSelectEmployee(null);
    if (createEmployee.fulfilled.match(createResult)) {
      await refreshDetail();
    }
  };

  const deSelectCandidate = async (employee: Employee) => {
    const result = await dispatch(getEmployee(String(employee._id)));
    const base = getEmployee.fulfilled.match(result)
      ? (result.payload as Employee)
      : employee;
    const values: Employee = {
      ...base,
      status: EmployeeStatus.new,
      notificationMessage: NotificationMessageTypes.deSelected as string,
      clearVisa: true,
    };
    const createResult = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(createResult)) {
      await refreshDetail();
    }
  };

  const allowEarlyPipeline = useMemo(() => {
    if (!selectedEmployee?._id) return false;
    const privileged = isPrivilegedTransaction(
      user?.roles as Role[],
      "Employees.View",
    );
    const st = selectedEmployee.status;
    return (
      privileged &&
      (st === EmployeeStatus.pending ||
        st === EmployeeStatus.new ||
        st === EmployeeStatus.selected ||
        st === EmployeeStatus.contracted)
    );
  }, [selectedEmployee, user?.roles]);

  const detailDocumentCards = useMemo(() => {
    if (!selectedEmployee) return [];

    // const allowContractDoc =
    //   privileged &&
    //   st !== undefined &&
    //   [
    //     EmployeeStatus.selected,
    //     EmployeeStatus.contracted,

    //   ].includes(st);

    // const allowWorkingVisaDoc =
    //   privileged &&
    //   st !== undefined &&
    //   [
    //     EmployeeStatus.visa_issued,
    //     EmployeeStatus.labour_issued,
    //     EmployeeStatus.flight_booked,
    //     EmployeeStatus.departed,
    //   ].includes(st);

    return [
      {
        title: "Stand Photo",
        photoType: PhotoTypes.Stand,
        src: selectedEmployee.standPhoto,
        icon: <Portrait fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Passport Photo",
        photoType: PhotoTypes.Passport,
        src: selectedEmployee.passportScan,
        icon: <CreditCard fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Medical Photo",
        photoType: PhotoTypes.Medical,
        src: selectedEmployee.medicalPhoto,
        icon: <LocalHospital fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Employment Contract",
        photoType: PhotoTypes.EmploymentContract,
        src: selectedEmployee.employmentContractScan,
        icon: <Description fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
        fileAccept: "image/*,.pdf,application/pdf",
      },
      {
        title: "Working Visa",
        photoType: PhotoTypes.WorkingVisa,
        src: selectedEmployee.workingVisaScan,
        icon: <BadgeOutlined fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
        fileAccept: "image/*,.pdf,application/pdf",
      },
      {
        title: "Original ID",
        photoType: PhotoTypes.OriginalId,
        src: selectedEmployee.originalIdScan,
        icon: <PermIdentity fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Emergency Contact ID",
        photoType: PhotoTypes.EmergencyContactId,
        src: selectedEmployee.emergencyContactIdScan,
        icon: <ContactEmergency fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Insurance",
        photoType: PhotoTypes.Insurance,
        src: selectedEmployee.insuranceScan,
        icon: <Shield fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "COC Certificate",
        photoType: PhotoTypes.CocCertificate,
        src: selectedEmployee.cocCertificateScan,
        icon: <VerifiedUser fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Education Certificate",
        photoType: PhotoTypes.EducationCertificate,
        src: selectedEmployee.educationCertificateScan,
        icon: <School fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
      {
        title: "Finger Print",
        photoType: PhotoTypes.FingerPrint,
        src: selectedEmployee.fingerPrintScan,
        icon: <Fingerprint fontSize="small" />,
        attachmentAllowed: allowEarlyPipeline,
      },
    ];
  }, [selectedEmployee, allowEarlyPipeline]);

  const searchBox = (
    <Paper
      elevation={0}
      sx={{
        mb: isSearchOnlyMode ? 0 : 2,
        p: isSearchOnlyMode ? { xs: 2.5, md: 3.5 } : { xs: 1.5, md: 2 },
        borderRadius: isSearchOnlyMode ? 4 : 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: isSearchOnlyMode
          ? "0 16px 40px rgba(25,118,210,0.14)"
          : "0 8px 24px rgba(0,0,0,0.05)",
        width: "100%",
        maxWidth: isSearchOnlyMode ? 760 : "100%",
        background: isSearchOnlyMode
          ? "linear-gradient(180deg, rgba(25,118,210,0.1) 0%, rgba(255,255,255,1) 120px)"
          : "background.paper",
      }}
    >
      {isSearchOnlyMode && (
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Find Employee Profile
        </Typography>
      )}
      <Autocomplete
        size={isSearchOnlyMode ? "medium" : "small"}
        options={searchOptions}
        filterOptions={(x) => x}
        loading={searchLoading}
        onChange={(_, value) => handleSelectEmployee(value)}
        inputValue={searchText}
        onInputChange={(_, newValue) => setSearchText(newValue)}
        getOptionLabel={(option) =>
          `${option.givenName || ""} ${option.surName || ""}`.trim() ||
          "Unnamed employee"
        }
        isOptionEqualToValue={(option, value) => option._id === value._id}
        noOptionsText={
          searchText.trim().length > 1
            ? "No employees found"
            : "Type 2+ letters"
        }
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={option.facePhoto || ""}
                sx={{ width: 28, height: 28 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {`${option.givenName || ""} ${option.surName || ""}`.trim() ||
                    "Unnamed employee"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.passportNumber || option._id}
                </Typography>
              </Box>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Find Employee"
            placeholder="Type employee name..."
            sx={{
              "& .MuiInputBase-root": {
                minHeight: isSearchOnlyMode ? 54 : undefined,
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {searchLoading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Paper>
  );

  if (isSearchOnlyMode) {
    return (
      <Container
        maxWidth="md"
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 3, md: 6 },
        }}
      >
        {searchBox}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {searchBox}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 5,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          background:
            "linear-gradient(180deg, rgba(25,118,210,0.12) 0%, rgba(25,118,210,0.03) 120px, rgba(255,255,255,1) 121px)",
        }}
      >
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {showLoading ? (
              <Stack spacing={1.2}>
                <Skeleton width={220} height={38} />
                <Skeleton width={180} height={32} />
              </Stack>
            ) : (
              <Stack spacing={1.25} width="100%">
                <Stack
                  spacing={1.5}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="primary"
                    textAlign={{ xs: "left", sm: "center" }}
                  >
                    {fullName}
                  </Typography>
                  {appliedCountryMeta && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        ml: 1,
                        color: "secondary.main",
                        fontSize: "1.2em",
                        fontWeight: 600,
                      }}
                    >
                      {appliedCountryMeta.flagCode && (
                        <Tooltip title={appliedCountryMeta.label}>
                          <Fab component="span" color="secondary">
                            <Avatar
                              alt={`${appliedCountryMeta.label} flag`}
                              src={`https://flagcdn.com/w320/${appliedCountryMeta.flagCode}.png`}
                              sx={{
                                height: 50,
                                width: 50,
                              }}
                            />
                          </Fab>
                        </Tooltip>
                      )}
                      {appliedCountryMeta.label}
                    </Box>
                  )}
                  <Box>
                    <Chip
                      color="secondary"
                      size="medium"
                      icon={<BadgeOutlined />}
                      label={`${
                        selectedEmployee?.status
                          ? te(selectedEmployee.status)
                          : "Unknown"
                      }`}
                      sx={{
                        height: 40,
                        px: 1.25,
                        borderRadius: 999,
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        boxShadow: "0 6px 14px rgba(25, 118, 210, 0.2)",
                        "& .MuiChip-label": {
                          px: 1.25,
                        },
                        "& .MuiChip-icon": {
                          fontSize: "1.1rem",
                        },
                      }}
                    />
                  </Box>
                  {selectedEmployee && (
                    <EmployeeFollowUpSummary employee={selectedEmployee} />
                  )}
                  {selectedEmployee?._id && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent={{ xs: "flex-start", sm: "center" }}
                      flexWrap="wrap"
                      spacing={0.5}
                      sx={{ pt: 0.25 }}
                    >
                      <EmployeeListRowActions
                        row={selectedEmployee}
                        activeStatusTab={activeStatusTab}
                        user={user}
                        showMedicalAction={showMedicalAction}
                        hideEmployeeDetailAction
                        iconSize="large"
                        onAgentSelect={openAgentSelectDialog}
                        onSelectForVisa={selectCandidateForVisa}
                        onDeselect={deSelectCandidate}
                        onChangeStatus={changeCandidateStatus}
                        onOpenMedical={openMedicalDialog}
                        onRestoreToAvailable={restoreToAvailable}
                        onOpenStatusHistory={openStatusHistoryDialog}
                        onOpenRemarks={openRemarksDialog}
                        onPlayVideo={playEmployeeVideo}
                        onOpenLimitedEdit={openLimitedEditDialog}
                        onStopProcess={(employee) => {
                          setStopEmployee(employee);
                          setOpenStop(true);
                        }}
                        onDelete={handleClickOpenDelete}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}
          </Paper>

          {!isValidId && !showLoading && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography color="text.secondary" textAlign="center">
                Provide a valid employee id to view details.
              </Typography>
            </Paper>
          )}

          {isValidId && !showLoading && !hasEmployee && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography color="text.secondary" textAlign="center">
                Employee detail is not available for this id.
              </Typography>
            </Paper>
          )}

          {hasEmployee && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                // maxWidth: 680,
                width: "100%",
                mx: "auto",
              }}
            >
              {/* <Typography variant="h6" fontWeight={700} gutterBottom textAlign="center">
                Employee Info
              </Typography>
              <Divider sx={{ mb: 2 }} /> */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1.5}>
                    <EmployeeMediaCard
                      title="Face Photo"
                      src={selectedEmployee?.facePhoto}
                      icon={<ImageOutlined fontSize="small" />}
                      compact
                      uploadInputIdPrefix="employee-detail-upload"
                      attachmentManage={
                        allowEarlyPipeline && selectedEmployee?._id
                          ? {
                              employeeId: selectedEmployee._id as string,
                              photoType: PhotoTypes.Face,
                              enabled: true,
                            }
                          : undefined
                      }
                    />
                    <EmployeeMediaCard
                      title="Employee Video"
                      src={selectedEmployee?.employeeVideo}
                      icon={<VideoLibrary fontSize="small" />}
                      compact
                      mediaKind="video"
                      uploadInputIdPrefix="employee-detail-upload"
                      attachmentManage={
                        allowEarlyPipeline && selectedEmployee?._id
                          ? {
                              employeeId: selectedEmployee._id as string,
                              photoType: PhotoTypes.Video,
                              enabled: true,
                              accept:
                                "video/mp4,video/webm,video/quicktime,video/*",
                            }
                          : undefined
                      }
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Labour ID"
                        value={selectedEmployee?.additionalData?.labourId}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Application Date"
                        value={formatDateValue(selectedEmployee?.createdAt)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Employee Telephone"
                        value={selectedEmployee?.address?.telephone}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Contact Name"
                        value={selectedEmployee?.contactPerson?.displayName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Contact Telephone"
                        value={
                          selectedEmployee?.contactPerson?.address?.telephone
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Broker Name"
                        value={selectedEmployee?.contactPerson?.kinship}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <EmployeeInfoField
                        label="Broker Telephone"
                        value={
                          selectedEmployee?.contactPerson?.address
                            ?.alternateTelephone
                        }
                      />
                    </Grid>
                  </Grid>
                  {hasEmployee && selectedEmployee?.visa && (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="h6" fontWeight={700}>
                            Visa Info
                          </Typography>
                          <Tooltip
                            title={
                              canPrintFirstPage
                                ? "Print First Page"
                                : "Requires passport number, visa, and application number"
                            }
                          >
                            <span>
                              <Button
                                color="secondary"
                                variant="contained"
                                size="small"
                                disabled={!canPrintFirstPage}
                                onClick={printFirstPage}
                                startIcon={<Print />}
                              >
                                Print First Page
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <EmployeeInfoField
                          label="Agent Name"
                          value={selectedEmployee?.partner?.displayName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Visa Number"
                          value={selectedEmployee?.visa?.visaNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Enjaz Number"
                          value={selectedEmployee?.embassyProcess?.enjazNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Employer Name"
                          value={selectedEmployee?.visa?.fullName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Employer Id"
                          value={selectedEmployee?.visa?.passportNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="City"
                          value={selectedEmployee?.visa?.address?.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Telephone"
                          value={selectedEmployee?.visa?.address?.telephone}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <EmployeeInfoField
                          label="Alternate Telephone"
                          value={
                            selectedEmployee?.visa?.owner?.address
                              ?.alternateTelephone
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}

          {(showLoading || hasEmployee) && (
            <Grid container spacing={2.5} justifyContent="center">
              {showLoading
                ? Array.from({ length: 11 }).map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`doc-skel-${index}`}>
                      <Skeleton variant="rounded" height={240} />
                    </Grid>
                  ))
                : detailDocumentCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.title}>
                      <EmployeeMediaCard
                        title={card.title}
                        src={card.src}
                        icon={card.icon}
                        compact
                        uploadInputIdPrefix="employee-detail-upload"
                        attachmentManage={
                          card.attachmentAllowed && selectedEmployee?._id
                            ? {
                                employeeId: selectedEmployee._id as string,
                                photoType: card.photoType,
                                enabled: true,
                                ...(card.fileAccept
                                  ? { accept: card.fileAccept }
                                  : {}),
                              }
                            : undefined
                        }
                      />
                    </Grid>
                  ))}
            </Grid>
          )}
        </Stack>
      </Paper>

      {/* {isValidId && effectiveEmployeeId && (
        <EmployeeComplaintsPanel employeeId={effectiveEmployeeId} />
      )}

      {hasEmployee && <EmployeeStatusHistory employee={selectedEmployee} />} */}

      {openDelete && (
        <Dialog
          open={openDelete}
          onClose={handleCloseDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">{t("delete")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              {t("delete")}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} autoFocus>
              Cancel
            </Button>
            <Button onClick={deleteEmployee}>{t("delete")}</Button>
          </DialogActions>
        </Dialog>
      )}

      {openChangeStatus && (
        <CustomDialog
          title={`${t("Change")} ${t("status")}`}
          isOpen={openChangeStatus}
          handleDialogClose={dialogCloseChangeStatus}
        >
          <ProcessStages
            currentEmployee={actionEmployee ?? selectedEmployee}
            handleDialogClose={dialogCloseChangeStatus}
          />
        </CustomDialog>
      )}

      {openMedical && (
        <CustomDialog
          title={te("medical_coc")}
          isOpen={openMedical}
          handleDialogClose={dialogCloseMedical}
          maxWidth={false}
          paperSx={{ width: 520, maxWidth: "95vw" }}
        >
          <MedicalDialog
            currentEmployee={actionEmployee ?? selectedEmployee}
            handleDialogClose={dialogCloseMedical}
            showCoc
          />
        </CustomDialog>
      )}

      {openStatusHistory && (
        <CustomDialog
          title={t("statusHistory")}
          isOpen={openStatusHistory}
          handleDialogClose={dialogCloseStatusHistory}
          maxWidth="md"
          fullWidth
          paperSx={{ width: "100%", maxWidth: 900 }}
        >
          <EmployeeStatusHistoryDialog
            employee={statusHistoryEmployee}
            loading={statusHistoryLoading}
          />
        </CustomDialog>
      )}

      {openRemarks && (
        <CustomDialog
          title={t("remarks")}
          isOpen={openRemarks}
          handleDialogClose={dialogCloseRemarks}
          maxWidth="sm"
          fullWidth
        >
          <EmployeeRemarksDialog
            employee={remarksEmployee}
            loading={remarksLoading}
            onSaved={(emp) => setRemarksEmployee(emp)}
          />
        </CustomDialog>
      )}

      {openStop && (
        <Dialog
          open={openStop}
          onClose={() => setOpenStop(false)}
          aria-labelledby="stop-dialog-title"
          aria-describedby="stop-dialog-description"
        >
          <DialogTitle id="stop-dialog-title">{t("stopProcess")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="stop-dialog-description">
              {stopEmployee?.givenName} {stopEmployee?.surName}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStop(false)} autoFocus>
              Cancel
            </Button>
            <Button color="error" onClick={handleStopEmployee}>
              {t("stopProcess")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openVisa && (
        <CustomDialog
          title={t("addAgreement")}
          isOpen={openVisa}
          handleDialogClose={dialogCloseVisa}
        >
          <VisaEntry
            currentEmployee={actionEmployee ?? selectedEmployee}
            handleDialogClose={dialogCloseVisa}
            officeContext={officePartnerContext}
          />
        </CustomDialog>
      )}

      {openLimitedEdit && (
        <CustomDialog
          title="Edit record"
          isOpen={openLimitedEdit}
          handleDialogClose={dialogCloseLimitedEdit}
          maxWidth="sm"
          fullWidth
        >
          <EmployeeLimitedEditDialog
            currentEmployee={actionEmployee ?? selectedEmployee}
            handleDialogClose={dialogCloseLimitedEdit}
          />
        </CustomDialog>
      )}

      {openAgentSelect && (
        <Dialog
          open={openAgentSelect}
          onClose={() => setOpenAgentSelect(false)}
          aria-labelledby="agent-select-dialog-title"
        >
          <DialogTitle id="agent-select-dialog-title">
            {t("only_select")}
          </DialogTitle>
          <DialogContent sx={{ pt: 1, minWidth: 280 }}>
            {user &&
              user.company &&
              (user.company.type === ClientTypes.Moderator ||
                user.company.type === ClientTypes.LocalAgency) && (
                <CompanyFilter
                  companyType={ClientTypes.ForeignAgent}
                  setCompany={setAgentForSelect}
                  includeAll={false}
                  value={agentForSelect}
                  offices={officePartnerContext?.foreignOffices}
                />
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAgentSelect(false)}>Cancel</Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={justSelect}
              disabled={!agentForSelect}
            >
              {t("only_select")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={Boolean(videoPreview)}
        onClose={() => setVideoPreview(null)}
        maxWidth="md"
      >
        <Box sx={{ p: 1.5, bgcolor: "background.default" }}>
          {videoPreview?.name && (
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ mb: 1, px: 0.5 }}
            >
              {videoPreview.name}
            </Typography>
          )}
          {videoPreview?.url && (
            <Box
              component="video"
              src={videoPreview.url}
              controls
              autoPlay
              playsInline
              sx={{
                width: { xs: "85vw", md: 720 },
                maxHeight: { xs: "70vh", md: 620 },
                borderRadius: 2,
                bgcolor: "common.black",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </Box>
      </Dialog>

      {processSuccess?.message && (
        <Toast severity="success">{processSuccess.message}</Toast>
      )}
      {processError?.message && (
        <Toast severity="error">{processError.message}</Toast>
      )}
    </Container>
  );
};
